import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { 
  Settings, 
  Bell,
  Moon,
  Sun,
  Database,
  Download,
  Users,
  Globe,
  DollarSign,
  Mail,
  Calendar,
  CheckCircle,
  Loader2,
  Link as LinkIcon,
  Trash2,
  LayoutGrid,
  Table,
  Kanban
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface PipelineStage {
  id: string;
  label: string;
  color: string;
  enabled: boolean;
}

interface CRMSettings {
  currency: string;
  timezone: string;
  defaultView: 'kanban' | 'table';
  notifications: {
    newDeals: boolean;
    stageChanges: boolean;
    mentions: boolean;
    weeklyReports: boolean;
  };
  pipelineStages: PipelineStage[];
  emailIntegration: boolean;
  calendarIntegration: boolean;
}

export function SettingsPage() {
  const { token } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const [settings, setSettings] = useState<CRMSettings>({
    currency: 'USD',
    timezone: 'America/New_York',
    defaultView: 'kanban',
    notifications: {
      newDeals: true,
      stageChanges: true,
      mentions: true,
      weeklyReports: false,
    },
    pipelineStages: [
      { id: 'new', label: 'New', color: '#3B82F6', enabled: true },
      { id: 'qualified', label: 'Qualified', color: '#8B5CF6', enabled: true },
      { id: 'proposal', label: 'Proposal', color: '#F59E0B', enabled: true },
      { id: 'negotiation', label: 'Negotiation', color: '#EF4444', enabled: true },
      { id: 'closed_won', label: 'Closed Won', color: '#10B981', enabled: true },
      { id: 'closed_lost', label: 'Closed Lost', color: '#6B7280', enabled: true },
    ],
    emailIntegration: false,
    calendarIntegration: false,
  });

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Save to localStorage for now (will sync to backend later)
      localStorage.setItem('crm_settings', JSON.stringify(settings));
      console.log('Settings saved:', settings);
      await new Promise(resolve => setTimeout(resolve, 500));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
    setIsLoading(false);
  };

  // Load saved settings on mount
  useEffect(() => {
    const saved = localStorage.getItem('crm_settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch { }
    }
  }, []);

  const updateNotification = (key: keyof typeof settings.notifications, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value }
    }));
  };

  const updateStage = (id: string, updates: Partial<PipelineStage>) => {
    setSettings(prev => ({
      ...prev,
      pipelineStages: prev.pipelineStages.map(s => s.id === id ? { ...s, ...updates } : s)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6 text-primary" />
            Workspace settings
          </h1>
          <p className="text-muted-foreground">
            Customize your workspace and preferences
          </p>
        </div>
        
        <Button 
          onClick={handleSave} 
          disabled={isLoading}
          className={cn(saveSuccess && "bg-green-600 hover:bg-green-700")}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : saveSuccess ? (
            <CheckCircle className="w-4 h-4 mr-2" />
          ) : null}
          {saveSuccess ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="preferences" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="data">Data</TabsTrigger>
        </TabsList>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="space-y-6">
          {/* General Preferences */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" /> General
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Currency</Label>
                <select 
                  className="w-full h-10 px-3 rounded-md border bg-background"
                  value={settings.currency}
                  onChange={(e) => setSettings(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="CAD">CAD (C$)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <select 
                  className="w-full h-10 px-3 rounded-md border bg-background"
                  value={settings.timezone}
                  onChange={(e) => setSettings(prev => ({ ...prev, timezone: e.target.value }))}
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
          </Card>

          {/* View Preferences */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" /> View Defaults
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Default Deal View</p>
                  <p className="text-sm text-muted-foreground">Choose your preferred layout for deals</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant={settings.defaultView === 'kanban' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSettings(prev => ({ ...prev, defaultView: 'kanban' }))}
                  >
                    Kanban
                  </Button>
                  <Button 
                    variant={settings.defaultView === 'table' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSettings(prev => ({ ...prev, defaultView: 'table' }))}
                  >
                    Table
                  </Button>
                </div>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isDarkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                  <div>
                    <p className="font-medium">Dark Mode</p>
                    <p className="text-sm text-muted-foreground">Toggle dark/light theme</p>
                  </div>
                </div>
                <Switch checked={isDarkMode} onCheckedChange={toggleDarkMode} />
              </div>
            </div>
          </Card>

          {/* Integrations */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <LinkIcon className="w-5 h-5" /> Integrations
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Email Integration</p>
                    <p className="text-sm text-muted-foreground">Sync with Gmail or Outlook</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">Connect</Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Calendar Integration</p>
                    <p className="text-sm text-muted-foreground">Sync meetings and follow-ups</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">Connect</Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Kanban className="w-5 h-5" /> Deal Stages
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Customize your pipeline stages. Disabled stages will be hidden from the pipeline.
            </p>
            <div className="space-y-3">
              {settings.pipelineStages.map((stage, index) => (
                <div key={stage.id} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: stage.color }}
                  />
                  <Input 
                    value={stage.label}
                    onChange={(e) => updateStage(stage.id, { label: e.target.value })}
                    className="flex-1 max-w-xs"
                  />
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-sm text-muted-foreground">Enabled</span>
                    <Switch 
                      checked={stage.enabled}
                      onCheckedChange={(checked) => updateStage(stage.id, { enabled: checked })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5" /> Notifications
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">New Deals</p>
                  <p className="text-sm text-muted-foreground">Notify when new deals are assigned to you</p>
                </div>
                <Switch 
                  checked={settings.notifications.newDeals}
                  onCheckedChange={(checked) => updateNotification('newDeals', checked)}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Stage Changes</p>
                  <p className="text-sm text-muted-foreground">Notify when deals move to a new stage</p>
                </div>
                <Switch 
                  checked={settings.notifications.stageChanges}
                  onCheckedChange={(checked) => updateNotification('stageChanges', checked)}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Mentions</p>
                  <p className="text-sm text-muted-foreground">Notify when you're mentioned in notes</p>
                </div>
                <Switch 
                  checked={settings.notifications.mentions}
                  onCheckedChange={(checked) => updateNotification('mentions', checked)}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Weekly Reports</p>
                  <p className="text-sm text-muted-foreground">Receive weekly pipeline summary</p>
                </div>
                <Switch 
                  checked={settings.notifications.weeklyReports}
                  onCheckedChange={(checked) => updateNotification('weeklyReports', checked)}
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Data Tab */}
        <TabsContent value="data" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" /> Data Management
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Download className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Export All Data</p>
                    <p className="text-sm text-muted-foreground">Download your complete workspace data as CSV</p>
                  </div>
                </div>
                <Button variant="outline">Export</Button>
              </div>
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Import Contacts</p>
                    <p className="text-sm text-muted-foreground">Bulk import from CSV or spreadsheet</p>
                  </div>
                </div>
                <Button variant="outline">Import</Button>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-red-500/20">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" /> Danger Zone
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Clear All Deals</p>
                  <p className="text-sm text-muted-foreground">Permanently delete all deals from your pipeline</p>
                </div>
                <Button variant="destructive" size="sm">Clear Deals</Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
